import React, { useState } from 'react'
import { Constants, GlobalConfig, I18n } from 'galarm-config'
import { FirebaseProxy, NavigationUtils } from 'galarm-ps-api'
import objGet from 'lodash/get'
import {
  ActionCreators,
  AlarmUtils,
  DateTimeUtils,
  TaskManager
} from 'galarm-shared'
import * as RNLocalize from 'react-native-localize'
import ShareAlarmImage from 'galarm-res/img/share-alarm.png'
import { Box, Button, Heading, Image, RadioButtonGroup, Text } from 'grommet'
import { useDispatch } from 'react-redux'

const ShareAlarm = ({ alarm, onClose }) => {
  const [relativeTimezone, setRelativeTimezone] = useState(false)
  const dispatch = useDispatch()

  const onSharePersonalAlarm = async () => {
    const nextDate = AlarmUtils.getNextDateForAlarm(
      alarm.date,
      alarm.endDate,
      alarm.repeatType,
      alarm.repeat,
      alarm.creatorTimezone
    )

    if (!nextDate) {
      NavigationUtils.showTransientAlert({
        message: I18n.t('alarmDoesntHaveNextDate')
      })
      return
    }

    // Reuse an existing link only if it was created by this provider.
    const existingLink = alarm.link || alarm.parentLink
    if (existingLink?.provider === 'cloudFunction') {
      if (existingLink.url && navigator.clipboard) {
        await navigator.clipboard.writeText(existingLink.url)
        NavigationUtils.showTransientAlert({
          message: I18n.t('alarmLinkCopiedToClipboard')
        })
      }
      return
    }

    let result
    try {
      const dateString = relativeTimezone
        ? DateTimeUtils.getDateTimeAsString(nextDate)
        : DateTimeUtils.getDateTimeAsStringWithTimezone(
            nextDate,
            alarm.creatorTimezone || RNLocalize.getTimeZone()
          )
      result = await TaskManager.addHttpsCloudTask('createSharedAlarmLink', {
        uid: GlobalConfig.uid,
        alarmId: alarm.id,
        alarmName: alarm.name,
        alarmDate: nextDate,
        relativeTimezone: relativeTimezone,
        alarmDateString: dateString,
        locale: I18n.currentLocale()
      })
    } catch (error) {
      console.log('Error creating shared alarm link', error)
      NavigationUtils.showTransientAlert({
        message: I18n.t('errorCreatingSharedAlarmLink')
      })
      return
    }

    const dynamicLink = result?.shareUrl
    if (!dynamicLink) {
      NavigationUtils.showTransientAlert({
        message: I18n.t('errorCreatingSharedAlarmLink')
      })
      return
    }

    const deepLink = `galarm://a/${alarm.id}`

    let firebaseUpdate = {}
    const linkId = GlobalConfig.firebaseHelpers.generatePushKey(
      GlobalConfig.firebaseHelpers.createRef('links')
    )
    // Write url and relativeTimezone as individual fields to avoid
    // overwriting link.subscribers (the list of followers).
    firebaseUpdate[`alarms/${alarm.id}/link/url`] = dynamicLink
    firebaseUpdate[`alarms/${alarm.id}/link/relativeTimezone`] =
      relativeTimezone
    firebaseUpdate[`alarms/${alarm.id}/link/provider`] = 'cloudFunction'
    firebaseUpdate[`alarms/${alarm.id}/lastUpdatedAt`] = Date.now()
    firebaseUpdate[`links/${linkId}`] = {
      dynamicLink: dynamicLink,
      deepLink: deepLink,
      relativeTimezone: relativeTimezone
    }
    await GlobalConfig.firebaseHelpers.updateData(
      GlobalConfig.rootFirebaseRef,
      firebaseUpdate
    )

    dispatch(
      ActionCreators.incrementShareableAlarmLinkCount(
        Constants.AlarmTypes.CASCADING
      )
    )
    FirebaseProxy.logEvent(
      Constants.UserAnalyticsEvents.CREATE_PERSONAL_ALARM_LINK,
      {}
    )
    console.log('dynamic link for alarm ' + dynamicLink)
    if (dynamicLink && navigator.clipboard) {
      await navigator.clipboard.writeText(dynamicLink)
      NavigationUtils.showTransientAlert({
        message: I18n.t('alarmLinkCopiedToClipboard')
      })
    }
  }

  let message,
    actionButtons = [],
    newLink = false

  if (alarm.link) {
    message = I18n.t('alreadySharedAlarmDescription', {
      url: alarm.link.url,
      count: Object.keys(objGet(alarm, 'link.subscribers', {})).length
    })

    actionButtons = [
      {
        text: I18n.t('shareLink'),
        onClick: async () => {
          onClose()
          await onSharePersonalAlarm()
        }
      },
      {
        text: I18n.t('close'),
        onClick: () => onClose()
      }
    ]
  } else if (alarm.parentLink) {
    message = I18n.t('shareParentAlarmDescription', {
      creatorName: alarm.parentLink.creatorName
    })
    actionButtons = [
      {
        text: I18n.t('shareLink'),
        onClick: async () => {
          onClose()
          await onSharePersonalAlarm()
        }
      },
      {
        text: I18n.t('close'),
        onClick: () => onClose()
      }
    ]
  } else {
    message = I18n.t('shareAlarmDescription')
    actionButtons = [
      {
        text: I18n.t('shareLink'),
        onClick: async () => {
          onClose()
          await onSharePersonalAlarm()
        }
      },
      {
        text: I18n.t('close'),
        onClick: () => onClose()
      }
    ]
    // Uncomment when relative timezone is released
    newLink = true
  }

  const alarmTimeWithCreatorTimezone =
    DateTimeUtils.getTimeAsStringWithTimezone(
      alarm.date,
      alarm.creatorTimezone || RNLocalize.getTimeZone()
    )

  const alarmTimeWithoutTimezone = DateTimeUtils.getTimeAsString(alarm.date)

  const relativeTimezoneOptions = [
    {
      id: 'relativetimezoneDisabled',
      label: I18n.t('alarmLinkNotRelativeToTimezone', {
        timeString: alarmTimeWithCreatorTimezone
      }),
      value: false
    },
    {
      id: 'relativetimezoneEnabled',
      label: I18n.t('alarmLinkRelativeToTimezone', {
        timeString: alarmTimeWithoutTimezone
      }),
      value: true
    }
  ]

  return (
    <Box align="center" gap="small">
      <Heading level={4}>{I18n.t('shareAlarm')}</Heading>
      <Image src={ShareAlarmImage} height="100px" width="100px" />
      <Text style={{ whiteSpace: 'pre-line' }}>{message}</Text>
      {newLink && (
        <Box fill="horizontal" margin={{ top: 'small' }}>
          <RadioButtonGroup
            name="relativetimezone"
            options={relativeTimezoneOptions}
            value={relativeTimezone}
            onChange={event =>
              setRelativeTimezone(event.target.value === 'true')
            }
          />
        </Box>
      )}
      <Box
        direction="row"
        alignSelf="end"
        gap="small"
        margin={{ top: 'small' }}>
        {actionButtons.map((button, index) => (
          <Button key={index} onClick={button.onClick} label={button.text} />
        ))}
      </Box>
    </Box>
  )
}

export default ShareAlarm
